import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { View, Text, Card } from "../base";
import { Icon } from "../base/Icon";
import { ComparisonDataPoint, COLORS } from "../../utils/chartData";

interface OutcomeComparisonChartProps {
  data: ComparisonDataPoint[];
  title?: string;
  metric?: string;
  yAxisLabel?: string;
  significanceData?: Record<string, { significance: string; confidence: number; pValue: number }>;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <View className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
        <Text className="font-medium mb-2">{label}</Text>
        {payload.map((entry: any, index: number) => (
          <View
            key={index}
            className="flex items-center space-x-2"
          >
            <View
              className="w-3 h-3 rounded"
              style={{ backgroundColor: entry.color }}
            />
            <Text className="text-sm">
              {entry.name}: {entry.value}
            </Text>
          </View>
        ))}
      </View>
    );
  }
  return null;
};

export const OutcomeComparisonChart: React.FC<OutcomeComparisonChartProps> = ({
  data,
  title = "Outcome Comparison",
  metric = "conversionRateValue",
  yAxisLabel = "Conversion Rate (%)",
  significanceData = {},
}) => {
  if (!data || data.length === 0) {
    return (
      <Card>
        <View className="p-6">
          <View className="flex items-center mb-4">
            <Icon
              name="bar-chart-2"
              size={20}
              className="mr-2 text-green-600 dark:text-green-400"
            />
            <Text className="text-lg font-semibold">{title}</Text>
          </View>
          <View className="flex justify-center items-center h-64">
            <Text className="text-gray-500 dark:text-gray-400">No data available</Text>
          </View>
        </View>
      </Card>
    );
  }

  return (
    <Card>
      <View className="p-6">
        <View className="flex items-center justify-between mb-4">
          <View className="flex items-center">
            <Icon
              name="bar-chart-2"
              size={20}
              className="mr-2 text-green-600 dark:text-green-400"
            />
            <Text className="text-lg font-semibold">{title}</Text>
          </View>
        </View>

        <ResponsiveContainer
          width="100%"
          height={300}
        >
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="variant" />
            <YAxis label={{ value: yAxisLabel, angle: -90, position: "insideLeft" }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar
              dataKey={metric}
              fill={COLORS[0]}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>

        {/* Statistical Significance Indicators */}
        {Object.keys(significanceData).length > 0 && (
          <View className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <Text className="text-sm font-medium mb-2">Statistical Significance:</Text>
            <View className="flex flex-wrap gap-2">
              {Object.entries(significanceData).map(([variant, significanceResult]) => (
                <View
                  key={variant}
                  className="flex items-center space-x-1"
                >
                  <Text className="text-xs font-medium">{variant}:</Text>
                  <View
                    className={`px-2 py-1 rounded text-xs ${
                      significanceResult.confidence >= 99
                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        : significanceResult.confidence >= 95
                          ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                          : significanceResult.confidence >= 90
                            ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                            : significanceResult.confidence >= 80
                              ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                              : significanceResult.confidence >= 70
                                ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                                : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                    }`}
                  >
                    <Text className="text-xs">{significanceResult.significance}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}
      </View>
    </Card>
  );
};
